/**
 * Servicio para la integración con VALId
 */

export interface VALIdUserData {
  identifier: string;
  name?: string;
  surnames?: string;
  email?: string;
  phone?: string;
  prefix?: string;
  method?: string;
  assuranceLevel?: string;
}

export interface VALIdLoginResponse {
  message: string;
  data: {
    valid_data: VALIdUserData;
    cognito_data: {
      username: string;
      email: string;
      dni: string;
      user_attributes: Record<string, string>;
    };
    access_token: string;
    cognito_tokens?: {
      IdToken: string;
      AccessToken: string;
      RefreshToken: string;
    };
  };
}

/**
 * Redirige al usuario a la página de autenticación de VALId
 * Construye la URL directamente sin pasar por el backend
 */
export const initiateValidAuth = (state?: string) => {
  // Configuración de VALId desde variables de entorno
  const VALID_BASE_URL = process.env.REACT_APP_VALID_BASE_URL || "https://valid-pre.aoc.cat";
  const VALID_CLIENT_ID = process.env.REACT_APP_VALID_CLIENT_ID || "";
  const VALID_REDIRECT_URI = process.env.REACT_APP_VALID_REDIRECT_URI || `${window.location.origin}/login`;
  
  // Construir parámetros de OAuth 2.0
  const params = new URLSearchParams({
    response_type: "code",
    client_id: VALID_CLIENT_ID,
    redirect_uri: VALID_REDIRECT_URI,
    scope: "autenticacio_usuari",
    access_type: "online",
    approval_prompt: "auto",
  });
  
  if (state) {
    params.append("state", state);
  }
  
  // Construir URL completa de VALId
  const authUrl = `${VALID_BASE_URL}/o/oauth2/auth?${params.toString()}`;
  
  console.log("Redirecting to VALId:", authUrl);
  
  // Redirigir al usuario directamente a VALId
  window.location.href = authUrl;
};

/**
 * Procesa el callback de VALId y completa el login
 * IMPORTANTE: Esta llamada NO requiere autenticación previa en Cognito
 */
export const processValidCallback = async (
  code: string
): Promise<VALIdLoginResponse> => {
  try {
    const backendUrl = process.env.REACT_APP_BACKEND_API_URL || "http://localhost:8000";
    const url = `${backendUrl}/valid/login?code=${encodeURIComponent(code)}`;
    
    console.log("Processing VALId callback with code:", code);
    
    // Llamada directa sin ApiFetcher (que requiere estar autenticado)
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Error ${response.status}: ${response.statusText}`);
    }
    
    const data = await response.json();
    console.log("VALId callback processed successfully:", data);
    
    return data;
  } catch (error) {
    console.error("Error processing VALId callback:", error);
    throw error;
  }
};

/**
 * Autentica al usuario en Cognito después del login con VALId
 * 
 * NOTA: Esta función necesita ser implementada según tu lógica específica
 * de Cognito. Actualmente, VALId retorna la información del usuario,
 * pero necesitas implementar el "auto-login" en Cognito.
 * 
 * Opciones:
 * 1. Usar el AdminInitiateAuth de Cognito (requiere backend)
 * 2. Usar contraseñas temporales
 * 3. Implementar custom authentication flow en Cognito
 */
export const authenticateWithCognitoAfterValid = async (
  username: string,
  // Aquí podrías necesitar tokens o contraseñas temporales
): Promise<void> => {
  // TODO: Implementar según tu estrategia de auto-login en Cognito
  console.log("Authenticating with Cognito for user:", username);
  
  // Por ahora, esta función es un placeholder
  // Necesitarás implementarla según tu estrategia específica
};

